import * as ngCommonHttp from '@angular/common/http';

import {
  Composite,
  CompositeOverrideInput,
  CompositeResponse,
  HeroComboErrorResponse,
  HeroComboRequest,
  HeroComboResponse,
  ProductGroupOverride,
} from '@interfaces/composite/1.0';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap, toArray } from 'rxjs/operators';

import { AbstractCompositeService } from '@abstract-services/composite/1.0/composite.abstract-service';
import { Injectable } from '@angular/core';
import { CatalogEntriesAPI, CompositeAPI } from '@constants/api';
import { CatalogItemType } from '@constants/catalogItem/1.0';
import { CATALOG_PAGE_SIZE_PRODUCTS_AND_COMBOS } from '@constants/pagination/1.0';
import { CatalogItemsService } from '@services/catalog-items/1.0/catalog-items.service';
import { PaginationService } from '@services/pagination/1.0/pagination.service';
import { PizzaContainer } from '@generated/tcea/models';

@Injectable({
  providedIn: 'root',
})
export class CompositeService extends AbstractCompositeService {
  getUrl;
  putUrl;
  postHeroUrl;
  patchUrl;
  itemType = CatalogItemType.COMPOSITE;

  // save the list of composites after fetching but keep track of which brand this is for so we can reset it
  cachedBrandId: string;
  fetchReplay: Observable<Composite[]>;

  public isCopying$ = new BehaviorSubject(false);
  public copiedComposite: Composite | undefined;

  constructor(
    private http: ngCommonHttp.HttpClient,
    private paginationService: PaginationService,
    private catalogItemsService: CatalogItemsService,
  ) {
    super();
    this.getUrl = CompositeAPI.GET;
    this.putUrl = CompositeAPI.PUT;
    this.postHeroUrl = CompositeAPI.POST_HERO;
    this.patchUrl = CatalogEntriesAPI.PATCH;
  }

  getComposites(brandId: string, includeDeleted: boolean = false): Observable<Composite[]> {
    // reset cache if this is a different brand ID
    if (this.cachedBrandId !== brandId) {
      this.catalogItemsService.resetMasterCache();
      this.cachedBrandId = brandId;
      this.fetchReplay = undefined;
    }

    if (!this.catalogItemsService.getCache(this.itemType)) {
      this.catalogItemsService.updateCache(this.itemType);

      let url = this.getUrl.replace(':tenantId', brandId);
      url += `?includeDeleted=${includeDeleted}`;

      return (this.fetchReplay = this.paginationService
        .fetchAllPages<CompositeResponse>(url, { pageSize: CATALOG_PAGE_SIZE_PRODUCTS_AND_COMBOS })
        .pipe(
          toArray(), // collect all pages into an array
          map((pages: CompositeResponse[]) =>
            // flatten all pages' composites into one array
            pages
              .filter((r) => r && r._embedded && r._embedded.composites)
              .reduce<Composite[]>((all, r) => all.concat(r._embedded.composites), []),
          ),
          tap((allComposites: Composite[]) => {
            // update cache once with the full list
            this.catalogItemsService.updateCache(this.itemType, allComposites);
          }),
          shareReplay(1),
          catchError((err: ngCommonHttp.HttpErrorResponse) => {
            this.catalogItemsService.clearCache(this.itemType);
            return throwError(err);
          }),
        ));
    } else if (this.catalogItemsService.getCache(this.itemType).length === 0) {
      return this.fetchReplay;
    } else {
      return of(this.catalogItemsService.getCache(this.itemType));
    }
  }

  save(catalogEntryId: string, composite: Composite, remove: string[] = []): Observable<Composite> {
    const url = `${this.patchUrl}/${catalogEntryId}`;
    const payload = {
      set: {
        ...composite,
      },
      remove,
    };
    return this.http.patch(url, payload).pipe(
      tap((response: any) => {
        let updatedComposite = composite;
        if (response && response.nutritionTexts) {
          updatedComposite = { ...composite, nutritionTexts: response.nutritionTexts } as any;
        }
        this.catalogItemsService.updateItemCache(updatedComposite, this.itemType);
      }),
      map((response: any) => {
        if (response && response.nutritionTexts) {
          return { ...composite, nutritionTexts: response.nutritionTexts } as any;
        }
        return composite;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  create(composite: Composite | PizzaContainer, brandId: string): Observable<Composite> {
    const url = this.getUrl.replace(':tenantId', brandId);
    return this.http.post(url, composite).pipe(
      map((response: Composite) => {
        return response;
      }),
      tap((c: Composite) => {
        if (this.cachedBrandId === brandId && this.catalogItemsService.getCache(this.itemType)) {
          this.catalogItemsService.addItemCache(c, this.itemType);
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  createHeroCombo(
    request: HeroComboRequest,
    dataGroupId: string,
    brandId: string,
  ): Observable<HeroComboResponse | HeroComboErrorResponse> {
    const url = this.postHeroUrl.replace(':dataGroupId', dataGroupId);
    return this.http.post(url, request).pipe(
      map((response: HeroComboResponse | HeroComboErrorResponse) => {
        return response;
      }),
      // TODO remove once backend is fixed
      tap(() => {
        this.catalogItemsService.clearCache(this.itemType);
      }),
      /**
       * TODO Composite in response does not contain productGroupContextData
       * Clearing cache instead of adding combo to cache until backend fix
       */
      // tap((response: HeroComboResponse | HeroComboErrorResponse) => {
      //     if (this.cachedBrandId === brandId && this.compositesCache) {
      //         let newCombo;
      //         if ('partialResponse' in response) {
      //             newCombo = response.partialResponse.heroCompositeCatalogEntry;
      //         } else if ('heroCompositeCatalogEntry' in response) {
      //             newCombo = response.heroCompositeCatalogEntry;
      //         }
      //         // ensure object is not empty
      //         if (newCombo && Object.keys(newCombo).length > 0) {
      //             this.compositesCache.push(newCombo);
      //         }
      //         // calling function needs to clear product and product group caches
      //         // unable to do it here due to circular references
      //     }
      // }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  saveOverrides(catalogEntryId: string, overrides: ProductGroupOverride[]): Observable<Composite> {
    const url = `${this.putUrl}${catalogEntryId}/overrides`;
    const BODY: CompositeOverrideInput = {
      payload: overrides,
    };
    return this.http.put(url, BODY).pipe(
      map((response: Composite) => {
        return response;
      }),
      tap((c: Composite) => {
        this.catalogItemsService.updateItemCache(c, this.itemType);
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  setCopyingState(isCopying: boolean) {
    this.isCopying$.next(isCopying);
  }

  /**
   * Get the current copying state
   * @returns boolean indicating if a composite is currently being copied
   */
  getCopyingState(): boolean {
    return this.isCopying$.value;
  }
}
